/**
 * Cross-Sell PDP Section
 *
 * Handles:
 * - Popup open / close
 * - Variant pill selection (border-only, no checkmarks)
 * - Selling plan radio toggle
 * - Unified ATC button (toggle popup first click, add-to-cart second click)
 * - Direct AJAX add-to-cart for single-variant cards
 * - Shopify recommendations fallback via <cross-sell-recommendations>
 */

(function () {
  "use strict";

  /* ──────────────────────────────────────────────
   * Helpers
   * ────────────────────────────────────────────── */
  // Format cents like Liquid's `money` filter. The card carries `123456789 | money`
  // (= 1234567.89 in the active market), from which prefix, suffix and separators are read.
  function formatMoney(cents, sample) {
    var m = /^([^\d]*)([\d.,\u00a0\u202f' ]*\d)([^\d]*)$/.exec((sample || "").trim());
    if (!m) throw new Error("cross-sell: bad data-money-sample: " + sample);
    var num = m[2];
    var last = Math.max(num.lastIndexOf("."), num.lastIndexOf(","));
    var hasDecimals = last !== -1 && num.length - last - 1 === 2;
    var decimalSep = hasDecimals ? num.charAt(last) : "";
    var thousandsSep = (hasDecimals ? num.slice(0, last) : num).replace(/\d/g, "").charAt(0);
    var amount = (cents / 100).toFixed(hasDecimals ? 2 : 0).split(".");
    amount[0] = amount[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);
    return m[1] + amount.join(decimalSep) + m[3];
  }

  function getCartSectionIds() {
    var ids = [];
    document.querySelectorAll("cart-items-component").forEach(function (el) {
      if (el.dataset.sectionId) ids.push(el.dataset.sectionId);
    });
    return ids.join(",");
  }

  // The theme cart repaints from section HTML carried by cart:update. When the add's own
  // reply has none (an add recovered after a lost reply), fetch those sections first.
  function withCartSections(data, sectionIds) {
    if (!sectionIds || (data && data.sections)) return Promise.resolve(data);
    return fetch(
      window.location.pathname + "?sections=" + encodeURIComponent(sectionIds),
      { headers: { Accept: "application/json" } },
    )
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(
        function (sections) {
          if (sections) data.sections = sections;
          return data;
        },
        function () {
          return data;
        },
      );
  }

  function fireCartUpdate(data) {
    document.dispatchEvent(
      new CustomEvent("cart:update", {
        detail: {
          data: {
            itemCount: data.item_count,
            sections: data.sections,
            source: "cross-sell-pdp",
          },
        },
      }),
    );
  }

  function addToCart(variantId, quantity, sellingPlanId) {
    var item = {
      id: parseInt(variantId, 10),
      quantity: quantity || 1,
    };
    if (sellingPlanId) {
      item.selling_plan = parseInt(sellingPlanId, 10);
    }

    var sectionIds = getCartSectionIds();

    if (
      window.BotaniqueCartAdd &&
      typeof window.BotaniqueCartAdd.addLines === "function"
    ) {
      return window.BotaniqueCartAdd
        .addLines([item], {
          attachFreePdf: true,
          // The warranty belongs to Kaching Cart: only its toggle line, and only into a
          // bag that has none yet (never the theme's own $2.90 line next to Kaching's).
          attachKachingWarranty: true,
          openDrawer: true,
          sections: sectionIds,
          sectionsUrl: window.location.pathname,
        })
        .then(function (data) {
          return withCartSections(data, sectionIds);
        })
        .then(function (data) {
          fireCartUpdate(data);
          return data;
        });
    }

    var body = { items: [item] };
    if (sectionIds) {
      body.sections = sectionIds;
      body.sections_url = window.location.pathname;
    }

    return fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok || data.status) {
            throw new Error(
              data.description || data.message || "Add to cart failed",
            );
          }
          fireCartUpdate(data);
          return data;
        });
      });
  }

  /* ──────────────────────────────────────────────
   * Popup management
   * ────────────────────────────────────────────── */

  var activePopup = null;

  function openPopup(wrapEl, toggleBtn) {
    if (activePopup && activePopup !== wrapEl) {
      closePopup(activePopup);
    }
    wrapEl.classList.add("is-open");
    if (toggleBtn) toggleBtn.setAttribute("aria-expanded", "true");
    activePopup = wrapEl;
    document.addEventListener("keydown", onEscKey);
    updateCardAtbPrice(toggleBtn, wrapEl);
  }

  function closePopup(wrapEl) {
    if (!wrapEl) return;
    wrapEl.classList.remove("is-open");
    var cardId = wrapEl.id.replace("popup-", "");
    var toggleBtn = document.querySelector(
      '[data-cs-toggle-popup="' + cardId + '"]',
    );
    if (toggleBtn) toggleBtn.setAttribute("aria-expanded", "false");
    if (activePopup === wrapEl) activePopup = null;
    document.removeEventListener("keydown", onEscKey);
  }

  function onEscKey(e) {
    if (e.key === "Escape" && activePopup) {
      closePopup(activePopup);
    }
  }

  var HEAD_COUNT_PRODUCT_IDS = ["10431348834469", "10754558296229"];

  /**
   * Read variant + selling plan from the popup and update ALL ATC button prices
   * (both the toggle button below the panel and the ATC inside the panel).
   */
  function updateCardAtbPrice(toggleBtn, popup) {
    if (!popup) return;

    var selectedPill = popup.querySelector(
      ".cross-sell-popup__variant-pill--selected",
    );
    var refBtn = toggleBtn || popup.querySelector("[data-cs-popup-atc]");
    var basePrice = selectedPill
      ? parseInt(selectedPill.getAttribute("data-cs-variant-price"), 10)
      : parseInt((refBtn ? refBtn.getAttribute("data-base-price") : null) || "0", 10);

    if (!basePrice) return;

    var finalPrice = basePrice;
    var isHeadCount = false;
    var omitPerWeek = false;
    var card = popup.closest("[data-product-id]");

    var checkedRadio = popup.querySelector("[data-cs-sp-radio]:checked");
    if (checkedRadio && checkedRadio.value) {
      var discount = parseInt(
        checkedRadio.getAttribute("data-cs-sp-discount") || "0",
        10,
      );
      if (discount > 0) {
        finalPrice = Math.round((basePrice * (100 - discount)) / 100);
      }
      var rawDivideQty = null;
      if (selectedPill) {
        rawDivideQty = selectedPill.getAttribute("data-cs-variant-divide-qty");
      } else if (refBtn) {
        rawDivideQty = refBtn.getAttribute("data-variant-divide-qty");
      }
      var divideMissing = selectedPill
        ? selectedPill.getAttribute("data-divide-missing")
        : refBtn
          ? refBtn.getAttribute("data-divide-missing")
          : null;
      var divideQty = 1;
      if (rawDivideQty !== null && rawDivideQty !== "") {
        divideQty = parseInt(rawDivideQty, 10);
      }
      if (!Number.isFinite(divideQty) || divideQty < 1) {
        console.error("cross-sell: invalid variant_quantity", rawDivideQty);
        return;
      }
      if (
        rawDivideQty !== null &&
        String(rawDivideQty).trim() !== String(divideQty)
      ) {
        console.error(
          "cross-sell: non-numeric variant_quantity",
          rawDivideQty,
        );
        return;
      }
      if (divideMissing === "true") {
        // blank variant_quantity metafield: the pack is not N four-week units,
        // so show the (discounted) pack price and skip the per-week figure
        omitPerWeek = true;
      } else {
        // sterile heads: variant_quantity = head count (per-head price, no weekly label)
        isHeadCount =
          !!card &&
          HEAD_COUNT_PRODUCT_IDS.indexOf(card.getAttribute("data-product-id")) !== -1;
        if (isHeadCount) {
          if (divideQty > 1) finalPrice = Math.round(finalPrice / divideQty);
        } else {
          // variant_quantity = number of 4-week units in the pack
          finalPrice = Math.round(finalPrice / (divideQty * 4));
        }
      }
    }

    var moneySample = card && card.getAttribute("data-money-sample");
    if (!moneySample) {
      console.error("cross-sell: missing data-money-sample", card);
      return;
    }
    var priceText =
      formatMoney(finalPrice, moneySample) +
      (checkedRadio && checkedRadio.value && !omitPerWeek
        ? isHeadCount
          ? "/head"
          : "/week"
        : "");

    var allPriceEls = popup.querySelectorAll("[data-cs-popup-price]");
    allPriceEls.forEach(function (el) {
      el.textContent = priceText;
    });
  }

  /* ──────────────────────────────────────────────
   * Event delegation
   * ────────────────────────────────────────────── */

  document.addEventListener("click", function (e) {
    var target = e.target;

    // ── Unified ATC / toggle button ──
    var toggleBtn = target.closest("[data-cs-toggle-popup]");
    if (toggleBtn) {
      e.preventDefault();
      if (toggleBtn.disabled || toggleBtn.classList.contains("is-loading"))
        return;

      var cardId = toggleBtn.getAttribute("data-cs-toggle-popup");
      var wrapEl = document.getElementById("popup-" + cardId);
      if (!wrapEl) return;

      var isOpen = wrapEl.classList.contains("is-open");

      if (!isOpen) {
        // First click: open the popup
        openPopup(wrapEl, toggleBtn);
      } else {
        // Second click (popup is open): add to cart
        var variantId = toggleBtn.getAttribute("data-variant-id");
        var sellingPlanId = "";
        var checkedRadio = wrapEl.querySelector("[data-cs-sp-radio]:checked");
        if (checkedRadio && checkedRadio.value) {
          sellingPlanId = checkedRadio.value;
        }

        toggleBtn.classList.add("is-loading");
        addToCart(variantId, 1, sellingPlanId)
          .then(function () {
            toggleBtn.classList.remove("is-loading");
            closePopup(wrapEl);
          })
          .catch(function (err) {
            console.error("Cross-sell add to cart error:", err);
            toggleBtn.classList.remove("is-loading");
          });
      }
      return;
    }

    // ── Popup panel ATC button (direct add-to-cart) ──
    var popupAtcBtn = target.closest("[data-cs-popup-atc]");
    if (popupAtcBtn) {
      e.preventDefault();
      if (popupAtcBtn.disabled || popupAtcBtn.classList.contains("is-loading"))
        return;

      var wrapEl = popupAtcBtn.closest("[data-cs-popup]");
      var variantId = popupAtcBtn.getAttribute("data-variant-id");
      var sellingPlanId = "";
      if (wrapEl) {
        var checkedRadio = wrapEl.querySelector("[data-cs-sp-radio]:checked");
        if (checkedRadio && checkedRadio.value) {
          sellingPlanId = checkedRadio.value;
        }
      }

      popupAtcBtn.classList.add("is-loading");
      addToCart(variantId, 1, sellingPlanId)
        .then(function () {
          popupAtcBtn.classList.remove("is-loading");
          if (wrapEl) closePopup(wrapEl);
        })
        .catch(function (err) {
          console.error("Cross-sell add to cart error:", err);
          popupAtcBtn.classList.remove("is-loading");
        });
      return;
    }

    // ── Close popup ──
    if (target.closest("[data-cs-popup-close]")) {
      var popup = target.closest("[data-cs-popup]");
      if (popup) closePopup(popup);
      return;
    }

    // ── Variant pill click (border selection, no checkmarks) ──
    var pill = target.closest("[data-cs-variant-id]");
    if (pill && pill.closest("[data-cs-popup]")) {
      var popup = pill.closest("[data-cs-popup]");
      var variantId = pill.getAttribute("data-cs-variant-id");
      var isAvailable =
        pill.getAttribute("data-cs-variant-available") === "true";

      // Update selected state (border only)
      popup.querySelectorAll("[data-cs-variant-id]").forEach(function (p) {
        p.classList.remove("cross-sell-popup__variant-pill--selected");
      });
      pill.classList.add("cross-sell-popup__variant-pill--selected");

      // Update ATC buttons' variant ID (both toggle and popup panel ATC)
      var cardId = popup.id.replace("popup-", "");
      var cardToggleBtn = document.querySelector(
        '[data-cs-toggle-popup="' + cardId + '"]',
      );
      if (cardToggleBtn) {
        cardToggleBtn.setAttribute("data-variant-id", variantId);
        cardToggleBtn.disabled = !isAvailable;
      }
      var popupAtcBtn = popup.querySelector("[data-cs-popup-atc]");
      if (popupAtcBtn) {
        popupAtcBtn.setAttribute("data-variant-id", variantId);
        popupAtcBtn.disabled = !isAvailable;
      }
      updateCardAtbPrice(cardToggleBtn, popup);

      // Update selling plan for the newly selected variant
      var variantSpId = pill.getAttribute("data-cs-sp-id");
      var spSection = popup.querySelector(".cross-sell-popup__selling-plans");
      var subscribeRadio = popup.querySelector(
        '[data-cs-sp-type="subscribe"] [data-cs-sp-radio]',
      );

      if (spSection) {
        if (variantSpId) {
          // Variant supports selling plans: update radio value, show section
          if (subscribeRadio) {
            subscribeRadio.value = variantSpId;
            var pillSpDiscount = pill.getAttribute("data-cs-sp-discount");
            if (pillSpDiscount != null) {
              subscribeRadio.setAttribute("data-cs-sp-discount", pillSpDiscount);
            }
          }
          spSection.classList.remove("is-hidden");
        } else {
          // Variant has no selling plan allocation: hide subscribe, reset to one-time
          spSection.classList.add("is-hidden");
          var onetimeRadio = popup.querySelector(
            '[data-cs-sp-type="onetime"] [data-cs-sp-radio]',
          );
          if (onetimeRadio) onetimeRadio.checked = true;
          // Reset visual state
          popup.querySelectorAll("[data-cs-sp-type]").forEach(function (opt) {
            opt.classList.remove("cross-sell-popup__sp-option--selected");
            var dot = opt.querySelector(".cross-sell-popup__sp-radio");
            if (dot) dot.classList.remove("cross-sell-popup__sp-radio--filled");
          });
          var onetimeLabel = popup.querySelector('[data-cs-sp-type="onetime"]');
          if (onetimeLabel) {
            onetimeLabel.classList.add("cross-sell-popup__sp-option--selected");
            var dot = onetimeLabel.querySelector(".cross-sell-popup__sp-radio");
            if (dot) dot.classList.add("cross-sell-popup__sp-radio--filled");
          }
          // Recalculate price without discount
          if (cardToggleBtn) updateCardAtbPrice(cardToggleBtn, popup);
        }
      }
      return;
    }

    // ── Selling plan radio ──
    var spLabel = target.closest("[data-cs-sp-type]");
    if (spLabel && spLabel.closest("[data-cs-popup]")) {
      e.preventDefault();
      e.stopPropagation();

      var popup = spLabel.closest("[data-cs-popup]");
      var radio = spLabel.querySelector("[data-cs-sp-radio]");
      if (radio) radio.checked = true;

      // Update radio visual state
      popup.querySelectorAll("[data-cs-sp-type]").forEach(function (opt) {
        opt.classList.remove("cross-sell-popup__sp-option--selected");
        var dot = opt.querySelector(".cross-sell-popup__sp-radio");
        if (dot) {
          dot.classList.remove("cross-sell-popup__sp-radio--filled");
        }
      });
      spLabel.classList.add("cross-sell-popup__sp-option--selected");
      var dot = spLabel.querySelector(".cross-sell-popup__sp-radio");
      if (dot) dot.classList.add("cross-sell-popup__sp-radio--filled");

      // Update the card ATC button price
      var cardId = popup.id.replace("popup-", "");
      var cardToggleBtn = document.querySelector(
        '[data-cs-toggle-popup="' + cardId + '"]',
      );
      updateCardAtbPrice(cardToggleBtn, popup);
      return;
    }

    // ── Direct add-to-cart (single-variant cards) ──
    var directBtn = target.closest("[data-cs-direct-atc]");
    if (directBtn) {
      e.preventDefault();
      if (directBtn.disabled || directBtn.classList.contains("is-loading"))
        return;

      var variantId = directBtn.getAttribute("data-variant-id");
      directBtn.classList.add("is-loading");
      addToCart(variantId, 1)
        .then(function () {
          directBtn.classList.remove("is-loading");
        })
        .catch(function (err) {
          console.error("Cross-sell add to cart error:", err);
          directBtn.classList.remove("is-loading");
        });
      return;
    }
  });

  /* ──────────────────────────────────────────────
   * Recommendations fallback custom element
   * ────────────────────────────────────────────── */

  class CrossSellRecommendations extends HTMLElement {
    #observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0]?.isIntersecting) return;
        observer.disconnect();
        this.#load();
      },
      { rootMargin: "0px 0px 400px 0px" },
    );

    connectedCallback() {
      this.#observer.observe(this);
    }

    disconnectedCallback() {
      this.#observer.disconnect();
    }

    #load() {
      var self = this;
      var url = this.dataset.url;
      var productId = this.dataset.productId;
      var sectionId = this.dataset.sectionId;
      var intent = this.dataset.intent || "related";
      var limit = this.dataset.limit || "8";

      if (!url || !productId) return;

      var fetchUrl =
        url +
        "?product_id=" +
        productId +
        "&section_id=" +
        sectionId +
        "&intent=" +
        intent +
        "&limit=" +
        limit;

      fetch(fetchUrl)
        .then(function (res) {
          if (!res.ok) throw new Error("Recommendations fetch failed");
          return res.text();
        })
        .then(function (html) {
          var div = document.createElement("div");
          div.innerHTML = html;
          var newContent = div.querySelector("#CrossSellPdp-" + sectionId);
          if (newContent) {
            var grid = newContent.querySelector(".cross-sell-pdp__grid");
            if (grid) {
              var currentGrid = self.querySelector(".cross-sell-pdp__grid");
              if (currentGrid) {
                currentGrid.innerHTML = grid.innerHTML;
              }
            }
          }
          // Re-init scrollbars & review counts after dynamic content load
          formatReviewCounts();
          initScrollbars();
          syncCrossSellSubscribeDefaultPrices();
        })
        .catch(function (err) {
          console.error("Cross-sell recommendations error:", err);
        });
    }
  }

  if (!customElements.get("cross-sell-recommendations")) {
    customElements.define(
      "cross-sell-recommendations",
      CrossSellRecommendations,
    );
  }

  /* ──────────────────────────────────────────────
   * Scrollbar indicator for mobile slider
   * ────────────────────────────────────────────── */

  function initScrollbars() {
    document.querySelectorAll(".cross-sell-pdp__grid").forEach(function (grid) {
      var scrollbar = grid.parentElement
        ? grid.parentElement.querySelector(".cross-sell-pdp__scrollbar")
        : null;
      if (!scrollbar) return;

      var thumb = scrollbar.querySelector(".cross-sell-pdp__scrollbar-thumb");
      if (!thumb) return;

      let isDragging = false;
      let startX = 0;
      let startScrollLeft = 0;

      function updateThumb() {
        var scrollWidth = grid.scrollWidth;
        var clientWidth = grid.clientWidth;

        if (scrollWidth <= clientWidth) {
          scrollbar.style.display = "none";
          return;
        }

        scrollbar.style.display = "";

        var thumbWidth = Math.max((clientWidth / scrollWidth) * 100, 15);
        var maxScroll = scrollWidth - clientWidth;
        var scrollPercent = maxScroll > 0 ? grid.scrollLeft / maxScroll : 0;
        var thumbLeft = scrollPercent * (100 - thumbWidth);

        thumb.style.width = thumbWidth + "%";
        thumb.style.left = thumbLeft + "%";
      }

      // DRAG START
      function startDrag(e) {
        isDragging = true;
        startX = e.touches ? e.touches[0].clientX : e.clientX;
        startScrollLeft = grid.scrollLeft;
        document.body.style.userSelect = "none";
      }

      // DRAG MOVE
      function onDrag(e) {
        if (!isDragging) return;

        let x = e.touches ? e.touches[0].clientX : e.clientX;
        let dx = x - startX;

        let scrollWidth = grid.scrollWidth;
        let clientWidth = grid.clientWidth;
        let maxScroll = scrollWidth - clientWidth;

        let scrollbarWidth = scrollbar.clientWidth;
        let thumbWidthPx = thumb.offsetWidth;
        let maxThumbMove = scrollbarWidth - thumbWidthPx;

        let scrollDelta = (dx / maxThumbMove) * maxScroll;

        grid.scrollLeft = startScrollLeft + scrollDelta;
      }

      // DRAG END
      function stopDrag() {
        isDragging = false;
        document.body.style.userSelect = "";
      }

      // EVENTS
      thumb.addEventListener("mousedown", startDrag);
      thumb.addEventListener("touchstart", startDrag, { passive: true });

      window.addEventListener("mousemove", onDrag);
      window.addEventListener("touchmove", onDrag, { passive: false });

      window.addEventListener("mouseup", stopDrag);
      window.addEventListener("touchend", stopDrag);

      grid.addEventListener("scroll", updateThumb, { passive: true });
      window.addEventListener("resize", updateThumb, { passive: true });

      updateThumb();
    });
  }

  /* ──────────────────────────────────────────────
   * Format review counts with commas on load
   * ────────────────────────────────────────────── */
  function formatReviewCounts() {
    document
      .querySelectorAll(".cross-sell-card__review-count")
      .forEach(function (el) {
        var text = el.textContent.trim();
        var match = text.match(/\((\d+)\s+Reviews\)/);
        if (match) {
          var num = parseInt(match[1], 10);
          var formatted = num.toLocaleString();
          el.textContent = "(" + formatted + " Reviews)";
        }
      });
  }

  function syncCrossSellSubscribeDefaultPrices() {
    document.querySelectorAll("[data-cs-popup]").forEach(function (wrapEl) {
      var subscribeRadio = wrapEl.querySelector(
        '[data-cs-sp-type="subscribe"] [data-cs-sp-radio]',
      );
      if (subscribeRadio && subscribeRadio.checked) {
        var cardId = wrapEl.id.replace("popup-", "");
        var toggleBtn = document.querySelector(
          '[data-cs-toggle-popup="' + cardId + '"]',
        );
        updateCardAtbPrice(toggleBtn, wrapEl);
      }
    });
  }

  function initAll() {
    formatReviewCounts();
    initScrollbars();
    syncCrossSellSubscribeDefaultPrices();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }
})();
